import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Code2, Palette, Search, Sparkles } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CONTACT } from "../i18n";
import { FadeUp, RevealLines } from "../components/Reveal";
import { Footer } from "../components/Contact";

const ICONS = [Code2, Palette, Search, Sparkles];

export default function EktirenPage() {
  const { t } = useLang();
  const e = t.ektiren;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-sans text-sm text-white/70 hover:text-white transition-colors"
            data-testid="ektiren-back-btn"
          >
            <ArrowLeft size={18} />
            {e.back}
          </Link>
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-xl tracking-tight text-white">VIP</span>
            <span className="font-sans text-xs tracking-[0.35em] uppercase text-[#d4af37]">
              Girne Taksi
            </span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden" data-testid="ektiren-hero">
        <div className="absolute inset-0 gold-glow" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="font-mono text-xs tracking-[0.3em] text-[#d4af37] mb-8">{e.overline}</p>
          </FadeUp>
          <h1 className="font-serif text-white leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tighter">
            <RevealLines lines={[e.title]} delay={0.1} />
          </h1>
          <FadeUp delay={0.3}>
            <p className="font-serif italic text-2xl md:text-3xl text-[#f7e7ce] mt-8 max-w-3xl">
              {e.lead}
            </p>
          </FadeUp>
          <FadeUp delay={0.4}>
            <p className="font-sans text-base md:text-lg text-white/60 mt-8 max-w-2xl leading-relaxed">
              {e.body}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-16 md:py-24 bg-[#0a0a0a] border-y border-white/10" data-testid="ektiren-services">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {e.services.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <FadeUp key={i} delay={i * 0.08}>
                <div className="hover-lift group h-full border border-white/10 hover:border-[#d4af37]/40 p-8 md:p-10">
                  <Icon size={30} className="text-[#d4af37] mb-6" strokeWidth={1.4} />
                  <h3 className="font-serif text-2xl md:text-3xl text-white mb-3">{s.title}</h3>
                  <p className="font-sans text-base text-white/60 leading-relaxed">{s.desc}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-36 overflow-hidden" data-testid="ektiren-cta">
        <div className="absolute inset-0 gold-glow" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center flex flex-col items-center">
          <FadeUp>
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl tracking-tight text-white max-w-3xl leading-tight">
              {e.ctaTitle}
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="font-sans text-base md:text-lg text-white/60 mt-8 max-w-xl">{e.ctaDesc}</p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <a
              href={CONTACT.ektiren}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 mt-12 font-sans font-medium text-black hover:bg-[#d4af37]"
              data-testid="ektiren-visit-btn"
            >
              {e.visit}
              <ArrowUpRight size={20} />
            </a>
          </FadeUp>
        </div>
      </section>

      <Footer />
    </div>
  );
}
